import { memo, useCallback } from 'react';

import { useTrackAgentThreadOpened } from '@air/analytics';
import { useAccount } from '@air/utils-auth';

import { WorkspaceAgentPanelMyTasksRowFrame } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowFrame';
import { WorkspaceAgentPanelMyTasksRowIcon } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowIcon';
import { WorkspaceAgentPanelMyTasksRowMetaContainer } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowMeta';
import { WorkspaceAgentPanelMyTasksRowOverflowMenu } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowOverflowMenu';
import { clearWorkspaceAgentOnboardingQueryParams } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentOnboardingRoleHandoff';
import { useOnboardingPreferencesStore } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

export const PrivateWorkspaceAgentOnboardingReviewTaskRow = memo(() => {
  const { data: account } = useAccount();
  const accountId = account?.id;
  const isArchived = useOnboardingPreferencesStore(
    (state) => !!accountId && state.workspaceAgentIntro[accountId]?.isReviewTaskArchived === true,
  );
  const archiveWorkspaceAgentIntroReviewTask = useOnboardingPreferencesStore(
    (state) => state.archiveWorkspaceAgentIntroReviewTask,
  );
  const clearWorkspaceAgentIntroComplete = useOnboardingPreferencesStore(
    (state) => state.clearWorkspaceAgentIntroComplete,
  );
  const { trackAgentThreadOpened } = useTrackAgentThreadOpened();

  const handleOpen = useCallback(() => {
    if (!accountId) {
      return;
    }

    trackAgentThreadOpened({
      is_first_run_walkthrough: true,
      status: isArchived ? 'archived' : 'active',
      type: 'existing',
    });
    clearWorkspaceAgentIntroComplete(accountId);
    useWorkspaceAgentPanelStore.getState().resetOnboarding();
    clearWorkspaceAgentOnboardingQueryParams();
  }, [accountId, clearWorkspaceAgentIntroComplete, isArchived, trackAgentThreadOpened]);

  const handleArchive = useCallback(() => {
    if (!accountId) {
      return;
    }

    archiveWorkspaceAgentIntroReviewTask(accountId);
  }, [accountId, archiveWorkspaceAgentIntroReviewTask]);

  return (
    <WorkspaceAgentPanelMyTasksRowFrame
      data-placement={isArchived ? 'archived' : 'active'}
      data-testid="WORKSPACE_AGENT_PANEL_MY_TASKS_ROW_REVIEW_ONBOARDING"
      icon={<WorkspaceAgentPanelMyTasksRowIcon isSelected={false} session={{ status: 'idle' }} />}
      meta={
        <WorkspaceAgentPanelMyTasksRowMetaContainer>
          <span>Just now</span>
        </WorkspaceAgentPanelMyTasksRowMetaContainer>
      }
      onClick={handleOpen}
      overflowMenu={
        <WorkspaceAgentPanelMyTasksRowOverflowMenu
          isArchiveDisabled={false}
          onArchive={handleArchive}
          onOpen={handleOpen}
          sessionTitle="Review onboarding"
          showArchive={!isArchived}
        />
      }
      preview={
        <p className="h-5 min-w-0 truncate pl-7 ads:text-secondary text-12 text-gray-11 leading-5">
          Watch it again, or archive it when you're done.
        </p>
      }
      title="Review onboarding"
    />
  );
});

PrivateWorkspaceAgentOnboardingReviewTaskRow.displayName = 'PrivateWorkspaceAgentOnboardingReviewTaskRow';
